import { type Moment } from 'moment'
import {
  defineComponent, type PropType, computed, ref, type Ref, type ComputedRef,
} from 'vue'
import useClickOutside from '../../../../composables/use-click-outside'
import PeriodCalendar from '../../calendars/period-calendar/period-calendar'
import './period-calendar-input.scss'

/**
 * Компонент представляет собой ввод даты-периода (с календарем).
 * Обладает различными настраиваемыми свойствами: возможность отключения, отображения текста, ошибок и пр.
 * <p>
 *   <a href="https://www.figma.com/file/Q4Z4CnFx4i0qhD5fbgnPpf/%F0%9F%92%BB-%D0%94%D0%B8%D0%B7%D0%B0%D0%B9%D0%BD-%D1%81%D0%B8%D1%81%D1%82%D0%B5%D0%BC%D0%B0-ARK-SPACE?type=design&node-id=6330%3A111555&mode=design&t=jJStrzMz5fbIZB2D-1" target="_blank">Дизайн</a>
 * </p>
 */
export default defineComponent({
  props: {
    /**
     *  Отключен ли ввод.
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Отображается ли лейбл (название) над инпутом.
     */
    isShowLabel: {
      type: Boolean,
      default: true,
    },
    /**
     * Отображает введеные даты под инпутом и над календарем.
     * Актуально при выборе нескольких периодов.
     */
    isShowCalendarInputs: {
      type: Boolean,
      default: true,
    },
    /**
     * Определяет, используется ли компонент внутри таблицы.
     */
    isForTable: {
      type: Boolean,
      default: false,
    },
    /**
     * Название поля, расположено над инпутом.
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Значение, отображаемое в поле ввода, если оно пусто.
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * Флаг, определяющий сторону инпута, относительно которой выравнивается положение календаря.
     */
    right: {
      type: Boolean,
      default: false,
    },
    /**
     * Массив строк с ошибками.
     */
    errors: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /**
     * Начальная дата.
     */
    start: {
      type: Object as PropType<Moment | undefined>,
    },
    /**
     * Конечная дата.
     */
    end: {
      type: Object as PropType<Moment | undefined>,
    },
    /**
     * Функция установки начальной даты.
     */
    onSetStart: {
      type: Function as PropType<(date: Moment | undefined) => void>,
      required: true,
    },
    /**
     * Функция установки конечной даты.
     */
    onSetEnd: {
      type: Function as PropType<(date: Moment | undefined) => void>,
      required: true,
    },
  },
  setup(props) {
    const isShowCalendar: Ref<boolean> = ref(false)
    const isSelected = computed(() => props.start && props.end)
    const calendar: Ref<HTMLDivElement | null> = ref(null)

    const startDate: ComputedRef<string> = computed(() => (props.start ? props.start.format('DD.MM.YYYY') : ''))
    const endDate: ComputedRef<string> = computed(() => (props.end ? props.end.format('DD.MM.YYYY') : ''))

    const showCalendar = (val: boolean) => {
      if (props.isDisabled && (val === true)) return

      isShowCalendar.value = val
    }

    const clear = () => {
      if (props.onSetStart) props.onSetStart(undefined)
      if (props.onSetEnd) props.onSetEnd(undefined)
    }

    const setStartDate = (val: Moment | undefined) => {
      if (props.onSetStart) {
        props.onSetStart(val)
      }
      if (val && endDate.value) isShowCalendar.value = false
    }

    const setEndDate = (val: Moment | undefined) => {
      if (props.onSetEnd) {
        props.onSetEnd(val)
      }
      if (val && startDate.value) isShowCalendar.value = false
    }

    const clearHandler = (e: MouseEvent) => {
      e.stopPropagation()
      clear()
    }

    useClickOutside(calendar, () => showCalendar(false))

    return () => (
      <div ref={calendar} onClick={() => showCalendar(!isShowCalendar.value)} class={`ark-ui-period-calendar-input ${props.isDisabled ? 'disabled' : ''} ${props.errors.length ? 'invalid' : ''}`}>
        { props.label && props.isShowLabel && <div class="ark-ui-period-calendar-input-label">{props.label}</div> }

        <div class={`ark-ui-period-calendar-input-head ${isShowCalendar.value ? 'is-opened' : ''} ${isSelected.value ? 'is-selected' : ''}`}>
          {
            isSelected.value && !props.isForTable
              ? <div class="ark-ui-period-calendar-input-selected-value">
                <div>{startDate.value}</div>
                {endDate.value && <div class="ark-ui-period-calendar-input-body-inputs-arrow" />}
                <div>{endDate.value}</div>
              </div>
              : <div class="ark-ui-period-calendar-input-head-placeholder">
                { props.placeholder ? props.placeholder : (
                  <div class="ark-ui-period-calendar-input-default-placeholder">
                    {startDate.value || '__.__.____'}
                    <div class="ark-ui-period-calendar-input-body-inputs-arrow" />
                    {endDate.value || '__.__.____'}
                  </div>
                )}
              </div>
          }
          {
            isSelected.value && !props.isForTable
              ? <div onClick={clearHandler} class="ark-ui-period-calendar-input-icon clear" />
              : <div class="ark-ui-period-calendar-input-icon calendar" />
          }
        </div>

        { !props.isDisabled && props.errors.map((error) => <div class='ark-ui-period-calendar-input-error'>{error}</div>) }

        <div
          class="ark-ui-period-calendar-input-body"
          style={`display: ${isShowCalendar.value ? 'block' : 'none'}; ${props.right ? 'right: 0;' : ''}`}
        >
          <div onClick={(e) => e.stopPropagation()} class="ark-ui-period-calendar-input-body-container">
            <PeriodCalendar
              isShowCalendarInputs={props.isShowCalendarInputs}
              start={props.start}
              end={props.end}
              right={props.right}
              onSetStart={setStartDate}
              onSetEnd={setEndDate}
            />
          </div>
        </div>
      </div>
    )
  },
})
